import Calendar16 from '@carbon/icons-react/es/calendar/16';
import SailboatOffshore24 from '@carbon/icons-react/es/sailboat--offshore/24';
import { Col, Typography } from 'antd';
import { isEmpty } from 'lodash';
import moment from 'moment';
import React from 'react';
import { FormattedMessage } from 'react-intl';
import { useMutation } from 'react-query';
import { TimeoffAPI } from 'api/timeoff';
import type { Timeoff, TimeoffResponse } from 'api/timeoff/types';
import { Button } from 'components/atoms/button';
import { Checkbox } from 'components/atoms/checkbox';
import { CustomDueDatePicker } from 'components/atoms/due-date-picker';
import { Form } from 'components/atoms/form';
import { TextArea } from 'components/atoms/input';
import { Select } from 'components/atoms/select';
import { VirtualizedMenuList } from 'components/atoms/select/custom-components';
import { toastService } from 'components/molecules/toast';
import {
  ButtonGroup,
  DrawerContent,
} from 'components/organisms/task-pane/task-detail';
import { useAccount } from 'hooks/use-account';
import { useTranslate, useTranslation } from 'hooks/use-translation';
import { useUser } from 'hooks/use-user';
import { useUsersQuery } from 'queries/members';
import { logger } from 'services';
import { callWorkerWithCleanup } from 'utils/call-worker-with-cleanup';
import { userOption } from 'utils/project-fields/construct-user-option';
import { TimeoffDate } from './timeoff-date';
import { TimeOffDrawerContentStyled } from './timeoff.styles';

type FormValues = {
  dateRange: string[];
  note?: string;
  notifyProjectOwners: boolean;
  notifyOthers?: number[];
};

type TimeoffFormProps = {
  handleClose: () => void;
};

export const TimeoffForm: React.FC<TimeoffFormProps> = ({ handleClose }) => {
  const { data: users = [] } = useUsersQuery();
  const timeoffErrorTitleFallback = useTranslation('failure');
  const [form] = Form.useForm();
  const handleDueDateChange = (range) => {
    let dateRange = range;

    if (range[0] == null && range[1] == null) {
      dateRange = null;
    }

    if (range[0] !== null && range[1] === null) {
      form.setFieldsValue({
        dateRange: [range[0], range[0]],
      });
    }

    if (range[1] !== null && range[0] === null) {
      form.setFieldsValue({
        dateRange: [range[1], range[1]],
      });
    }

    form.setFieldsValue({
      dateRange,
    });
  };

  const translate = useTranslate();
  const timeoffSucessTitte = translate(
    'settings.profile.timeoff.creation.sucess.title',
  );
  const timeoffSucessDescription = translate(
    'settings.profile.timeoff.creation.sucess.description',
  );

  const createTimeoffMutation = useMutation<TimeoffResponse, any, Timeoff>(
    (payload: Timeoff) => {
      return TimeoffAPI.createTimeoff(payload);
    },
    {
      onSuccess: (timeoffData) => {
        toastService({
          title: timeoffSucessTitte,
          message: timeoffSucessDescription,
        }).success();

        const worker = callWorkerWithCleanup();

        if (timeoffData?.userId) {
          worker.workerApi
            .addUserTimeoff({
              timeoffData,
              userId: timeoffData.userId,
            })
            .catch((err) => {
              logger.error(
                `Error occured while adding timeoff to indexedDb timeoffId: ${timeoffData.timeOffId}`,
                err,
              );
            })
            .finally(() => {
              worker.cleanup();
            });
        }

        form.resetFields();
        handleClose();
      },
      onError: (error) => {
        toastService({
          title:
            error.response.data.errors[0].reason ?? timeoffErrorTitleFallback,
          message: '',
        }).error();
      },
    },
  );
  const user = useUser();
  const account = useAccount();

  const handleSubmit = (values) => {
    createTimeoffMutation.mutate({
      accountId: account.accountId,
      timeOffStart: values.dateRange[0] ?? values.dateRange[1],
      timeOffEnd: values.dateRange[1] ?? values.dateRange[0],
      note: values.note,
      notifyProjectOwners: values.notifyProjectOwners,
      notifyOthers: values.notifyOthers?.map((x) => x.value) ?? [],
      deleted: false,
      userId: user.user.userId,
    });
  };

  const handleCancel = () => {
    form.resetFields();
    handleClose();
  };

  const today = moment().format('YYYY-MM-DD');

  const initialValues: FormValues = React.useMemo(() => {
    return {
      dateRange: [today, today],
      note: '',
      notifyProjectOwners: true,
    };
  }, [today]);

  return (
    <TimeOffDrawerContentStyled>
      <Form
        initialValues={initialValues}
        layout="vertical"
        form={form}
        onFinish={handleSubmit}
      >
        <DrawerContent.Title data-cy="settings.profile.timeoff.add_timeoff">
          <div className="timeoff-header">
            <SailboatOffshore24 />
            <FormattedMessage
              tagName="span"
              id="settings.profile.timeoff.drawer.title"
            />
          </div>
        </DrawerContent.Title>

        <DrawerContent.Content>
          <Form.Item
            name="dateRange"
            label={useTranslation(
              'settings.profile.timeoff.drawer.form.timeoff_date',
            )}
            rules={[
              {
                required: true,
              },
            ]}
          >
            <CustomDueDatePicker
              disabledDate={(date) =>
                // disable if more than 1 year

                date &&
                (date.day() === 0 ||
                  date.day() === 6 ||
                  date.isAfter(moment().add(1, 'year')))
              }
              isRangePicker
              renderTrigger={(togglePickerVisibility) => (
                // eslint-disable-next-line jsx-a11y/click-events-have-key-events
                <li
                  role="menuitem"
                  onClick={() => {
                    togglePickerVisibility();
                  }}
                  className="timeoff-date-picker-trigger"
                >
                  <TimeoffDate
                    startDate={
                      form.getFieldValue('dateRange')
                        ? form.getFieldValue('dateRange')[0]
                        : undefined
                    }
                    dueDate={
                      form.getFieldValue('dateRange')
                        ? form.getFieldValue('dateRange')[1]
                        : undefined
                    }
                  />
                  <Calendar16 />
                </li>
              )}
              disallowSingleDate={false}
              hideModifyPicker
              startDate={
                form.getFieldValue('dateRange')
                  ? form.getFieldValue('dateRange')[0]
                  : undefined
              }
              dueDate={
                form.getFieldValue('dateRange')
                  ? form.getFieldValue('dateRange')[1]
                  : undefined
              }
              onDateChange={handleDueDateChange}
            />
          </Form.Item>

          <FormattedMessage id="placeholder.input">
            {(message: string) => (
              <Form.Item
                name="note"
                label={
                  <FormattedMessage id="settings.profile.timeoff.drawer.form.note" />
                }
              >
                <TextArea
                  placeholder={message}
                  autoSize={{ minRows: 3, maxRows: 3 }}
                />
              </Form.Item>
            )}
          </FormattedMessage>

          <Form.Item
            name="notifyProjectOwners"
            valuePropName="checked"
            initialValue
          >
            <Checkbox>
              <FormattedMessage id="settings.profile.timeoff.drawer.form.checkbox.notify" />
            </Checkbox>
          </Form.Item>

          <Col className="timeoff-user-typeahead">
            <Form.Item
              name="notifyOthers"
              label={
                <FormattedMessage id="settings.profile.timeoff.drawer.form.autosuggest.notify_others" />
              }
            >
              <Select
                type="user"
                isMulti
                isClearable
                closeMenuOnSelect={false}
                components={{
                  DropdownIndicator: () => null,
                  IndicatorSeparator: () => null,
                  MenuList: VirtualizedMenuList,
                }}
                noOptionsMessage={({ inputValue }) => {
                  const input = inputValue ? inputValue.trim() : undefined;

                  if (isEmpty(users) && !input) {
                    return <FormattedMessage id="no.options" />;
                  }

                  return (
                    <FormattedMessage
                      id="no.matches.found"
                      values={{
                        b: (chunks) => (
                          <Typography.Text strong>
                            {input ?? chunks}
                          </Typography.Text>
                        ),
                      }}
                    />
                  );
                }}
                options={(users ?? []).map(userOption)}
              />
            </Form.Item>
          </Col>
        </DrawerContent.Content>
        <DrawerContent.Footer>
          <ButtonGroup $reversed>
            <Button
              htmlType="submit"
              data-cy="profile.timeoff-drawer.add"
              iconPlacement="right"
              type="primary"
            >
              <FormattedMessage id="settings.profile.timeoff.drawer.form.button.apply" />
            </Button>
            <Button
              onClick={handleCancel}
              data-cy="profile.timeoff-drawer.clear"
            >
              <FormattedMessage id="settings.profile.timeoff.drawer.form.button.cancel" />
            </Button>
          </ButtonGroup>
        </DrawerContent.Footer>
      </Form>
    </TimeOffDrawerContentStyled>
  );
};
